import React, { useEffect, useMemo, useRef } from 'react';
import { Box, HealthPlanStatuses, Preloader } from '@common-fe/common-fe';
import styled from 'styled-components';

import { OrganizationTypes } from '@/common/constants';
import { Accordion } from '@/components/elements/Accordion';
import { useCurrentOrganization } from '@/modules/core';
import { useHealthPlanNameStore } from '@/modules/HealthPlan/HealthPlanSetup/BasicProperties/HealthPlanName/stores';
import { useServiceConfigs } from '@/modules/HealthPlan/HealthPlanSetup/ComplexConfiguration/EnrollmentMatrix/Tiers/queries';
import { useHasUnselectedServiceTypes } from '@/modules/HealthPlan/HealthPlanSetup/hooks';
import { usePlansByPlanYearQuery } from '@/modules/HealthPlan/queries';
import theme from '@/styles/theme';

import { useActiveHealthPlanUpdateModalStore } from './ActiveHealthPlanUpdateModal/stores';
import { useProcessingRuleTiersStore } from './ComplexConfiguration/EnrollmentMatrix/stores/useProcessingRuleTiersStore';
import useEditMode from './hooks/useEditMode';
import ActivityLogSection from './ActivityLogSection';
import BasicProperties from './BasicProperties';
import ComplexConfiguration from './ComplexConfiguration';
import CoverageLevels from './CoverageLevels';
import FinalSetup from './FinalSetup';
import PlanSetupWrapper from './HealthPlanSetupWrapper';
import PlanTopbar from './HealthPlanTopbar';
import { useCurrentHealthPlan, useHealthPlanSetupSetter } from './hooks';
import {
  useIsEditableTemplateStore,
  usePlanLoadingStore,
  usePreviewStore,
  useSubmitFormStore,
} from './stores';

const StyledBox = styled(Box)`
  flex: 1 0;
`;

const FixedWrapper = styled(Box)`
  position: sticky;
  top: 0;
  z-index: 12;
`;

// const TIMEOUT = 1000;
interface Props {
  defaultEditMode?: boolean;
  isTemplateMode?: boolean;
}
const HealthPlan: React.FC<Props> = ({ defaultEditMode, isTemplateMode }) => {
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const activateButtonRef = useRef<HTMLButtonElement>(null);
  const reviewButtonRef = useRef<HTMLButtonElement>(null);
  const { healthPlanStatus, planName, arePlansEditable, planYearId } = useCurrentHealthPlan(isTemplateMode);
  const healthPlanTypes = useHealthPlanNameStore((state) => state.state.healthPlanTypes);
  const { data: serviceConfigs } = useServiceConfigs();
  const { hasUnselectedServiceTypes } = useHasUnselectedServiceTypes(serviceConfigs, healthPlanTypes);

  const handleSetModalState = useActiveHealthPlanUpdateModalStore((store) => store.handleSetState);
  const handleResetModalState = useActiveHealthPlanUpdateModalStore((store) => store.handleReset);
  const handleSetIsEditableTemplate = useIsEditableTemplateStore((state) => state.setIsEditableTemplate);

  const previewMode = usePreviewStore((state) => state.previewMode);
  const isTemplateModeApplied = useMemo(() => isTemplateMode, [isTemplateMode]);
  const isHealthPlanActive = useMemo(
    () =>
      healthPlanStatus === HealthPlanStatuses.ACTIVE ||
      healthPlanStatus === HealthPlanStatuses.PENDING_ACTIVE,
    [healthPlanStatus]
  );

  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);
  const loading = usePlanLoadingStore((state) => state.loading);
  const isEditMode = useEditMode(defaultEditMode, isHealthPlanActive, true);
  const isEditModeApplied = useMemo(() => isEditMode && !previewMode, [isEditMode, previewMode]);
  const isEditableTemplate = useMemo(
    () => isTemplateModeApplied && isEditMode && isHealthPlanActive,
    [isHealthPlanActive, isTemplateModeApplied, isEditMode]
  );
  const { formatedOptions: planYearFormatedOptions } = usePlansByPlanYearQuery({
    id: planYearId,
  });
  const { handleReset } = useHealthPlanSetupSetter();
  const resetTiers = useProcessingRuleTiersStore((state) => state.resetTiers);
  const handleResetSubmit = useSubmitFormStore((state) => state.reset);
  useEffect(() => {
    handleSetModalState({ healthPlanName: planName });
    handleSetIsEditableTemplate(!!isEditableTemplate);
  }, [handleSetModalState, planName, handleSetIsEditableTemplate, isEditableTemplate]);

  useEffect(
    () => () => {
      handleResetSubmit();
      handleReset();
      handleResetModalState();
      handleSetIsEditableTemplate(false);
      resetTiers();
    },
    [handleReset, handleResetSubmit, handleResetModalState, handleSetIsEditableTemplate, resetTiers]
  );
  if (loading) {
    return <Preloader testId="preloader" />;
  }

  return (
    <PlanSetupWrapper isTemplateModeApplied={isTemplateModeApplied} isHealthPlanActive={isHealthPlanActive}>
      <FixedWrapper height={{ min: 'fit-content' }} width="100%" background={{ color: 'canvas' }}>
        <PlanTopbar
          saveButtonRef={saveButtonRef}
          activateButtonRef={activateButtonRef}
          reviewButtonRef={reviewButtonRef}
          viewMode={!isEditMode}
          planStatus={healthPlanStatus}
          isTemplateMode={isTemplateMode}
          isHealthPlanActive={isHealthPlanActive}
          hasUnselectedServiceTypes={hasUnselectedServiceTypes}
        />
        <Accordion />
      </FixedWrapper>
      <StyledBox width={theme.defaultContentWidth}>
        <BasicProperties
          isEditMode={isEditModeApplied}
          isTemplateMode={isTemplateModeApplied}
          isHealthPlanActive={isHealthPlanActive}
          arePlansEditable={arePlansEditable}
          planYearFormatedOptions={planYearFormatedOptions}
        />
        <ComplexConfiguration
          isEditMode={isEditModeApplied}
          isTemplateMode={isTemplateModeApplied}
          serviceConfigs={serviceConfigs}
        />
        <CoverageLevels isEditMode={isEditModeApplied} isTemplateMode={isTemplateModeApplied} />
        <ActivityLogSection />
        {!previewMode && !isSubsidiary && (
          <FinalSetup
            saveButtonRef={saveButtonRef}
            activateButtonRef={activateButtonRef}
            reviewButtonRef={reviewButtonRef}
            isTemplateMode={isTemplateMode}
            planStatus={healthPlanStatus}
            hasUnselectedServiceTypes={hasUnselectedServiceTypes}
            serviceConfigs={serviceConfigs}
            isEditMode={isEditModeApplied}
            isEditableTemplate={isEditableTemplate}
          />
        )}
      </StyledBox>
    </PlanSetupWrapper>
  );
};
export default HealthPlan;
